<template>
  <div id="app">
    <v-app :style="{ background: $vuetify.theme.themes[theme].background }">
      <ProgressBar />
      <template v-if="isLoggedIn">
        <NavDrawer
          :drawer-open="appDrawerOpen"
          @update-drawer="updateDrawerStatus"
        />
        <NavBar @toggle-app-drawer="appDrawerOpen = !appDrawerOpen" />
      </template>
      <transition
        name="ill-app-fade"
        mode="out-in"
      >
        <v-main class="mt-12">
          <router-view @display-alert="callDisplayAlert" />
        </v-main>
      </transition>
      <RecordEditorCreateDialog v-if="createRecordDialogOpen" />
      <AlertBoxV2 />
      <DownloadNotifications />
      <EntriesMassAssignmentNotifications />
      <v-dialog
        v-model="showWarning"
        persistent
        width="522"
      >
        <v-card id="inactivity-warning">
          <v-card-title primary-title>
            <v-icon
              color="white"
              class="mr-2"
            >
              notification_important
            </v-icon>Warning
          </v-card-title>

          <v-container>
            <p>You will be logged out in {{ refreshTokenExpiresAtWarnLimit }} seconds.</p>
          </v-container>

          <v-card-actions>
            <v-spacer />
            <v-btn
              color="primary"
              @click="keepActive"
            >
              Remain active
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <PEQuickEdit v-if="peSharedQESliderStatus" />
      <DocketEntryEditorDialog v-if="isDocketEntryEditorVisible" />
      <DocketEntryEditorInviteDialog v-if="isDocketEntryEditorInviteVisible" />
      <TextMessageEditorDialog v-if="isTextMessageEditorVisible" />
      <NewsDialog v-model="showNewsDialog" />
      <v-dialog
        v-model="showAuthenticationFormsDialog"
        persistent
        content-class="elevation-0"
        width="522"
      >
        <AuthenticationForms
          v-if="showAuthenticationFormsDialog"
          @on-close-auth-dialog="_ => onCloseLoginForm()"
        />
      </v-dialog>
    </v-app>
  </div>
</template>

<script>
import * as Sentry from '@sentry/vue'
import { mapActions, mapGetters, mapMutations } from 'vuex'

import { alertHandler } from '@/alertHandler'
import { createRgbVarsForThemes } from '@/utilities/hex-to-rgba'
import { subscribeToAfterDeployNoticesChannel } from '@/channels/after_deploy_notices_channel'

import { DOCKET_ENTRY_NAMESPACE } from '@/store/docket-entry'
import { PARTY_NAMESPACE } from '@/store/party'

// import AlertBox from '@/components/Misc/AlertBox.vue'
import AlertBoxV2 from './components/Misc/AlertBoxV2.vue'
import AuthenticationForms from '@/components/Authentication/AuthenticationForms.vue'
import DocketEntryEditorDialog from '@/components/DocketEntry/Editor/DocketEntryEditorDialog.vue'
import DocketEntryEditorInviteDialog from '@/components/DocketEntry/Editor/Invite/DocketEntryEditorInviteDialog.vue'
import DownloadNotifications from '@/components/Downloads/DownloadNotifications.vue'
import EntriesMassAssignmentNotifications from '@/components/DocketEntry/EntriesMassAssignmentNotifications.vue'
import NavBar from '@/components/Nav/Bar.vue'
import NavDrawer from '@/components/Nav/Drawer.vue'
import NewsDialog from '@/components/Dialogs/Common/NewsDialog.vue'
import PEQuickEdit from '@/components/Party/PartyEdit/PEQuickEdit.vue'
import ProgressBar from '@/components/Nav/ProgressBar.vue'
import RecordEditorCreateDialog from '@/components/Record/Editor/RecordEditorCreateDialog.vue'
import TextMessageEditorDialog from '@/components/DocketEntry/TextMessageEditor/TextMessageEditorDialog.vue'

// How often to check the session against the refresh token expiry.
const SESSION_CHECK_INTERVAL_MS = 15 * 1000
const SESSION_EXTENSION_EVENT_KEY = 'sessionExtended'
const SESSION_EXTENSION_LOCK_KEY = 'sessionExtensionLock'
const SESSION_EXTENSION_LOCK_TTL_MS = 10 * 1000
const SESSION_EXTENSION_WAIT_MS = 5 * 1000
const SESSION_WARNING_EVENT_KEY = 'sessionWarning'

export default {
  name: 'App',

  components: {
    // AlertBox,
    AlertBoxV2,
    AuthenticationForms,
    DocketEntryEditorDialog,
    DocketEntryEditorInviteDialog,
    DownloadNotifications,
    EntriesMassAssignmentNotifications,
    NavBar,
    NavDrawer,
    NewsDialog,
    PEQuickEdit,
    ProgressBar,
    RecordEditorCreateDialog,
    TextMessageEditorDialog,
  },

  setup () {
    return {
      ...alertHandler(),
    }
  },

  data () {
    return {
      appDrawerOpen: false,
      deferredLogoutTimeoutId: null,
      extendingSession: false,
      notifications: {
        result: [],
      },

      sessionCheckIntervalId: null,
      sessionExtensionWaitTimeoutId: null,
      sessionTabId: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      showAuthenticationFormsDialog: false,
      showNewsDialog: false,
      showWarning: false,
      warnCountdownIntervalId: null,
    }
  },

  computed: {
    sessionActivityWindowMs () {
      return (this.sessionActivityWindowIn || 0) * 1000
    },

    sessionWarnBeforeMs () {
      return Number(this.sessionWarnBeforeIn || 0) * 1000
    },

    theme () {
      return this.$vuetify.theme.dark ? 'dark' : 'light'
    },

    ...mapGetters([
      'createRecordDialogOpen',
      'currentUser',
      'isLoggedIn',
      'isSessionInactive',
      'refreshTokenExpiresAt',
      'refreshTokenExpiresAtWarnLimit',
      'sessionActivityWindowIn',
      'sessionReturnTo',
      'sessionWarnBeforeIn',
    ]),

    ...mapGetters(DOCKET_ENTRY_NAMESPACE, [
      'isDocketEntryEditorInviteVisible',
      'isDocketEntryEditorVisible',
      'isTextMessageEditorVisible',
    ]),

    ...mapGetters(PARTY_NAMESPACE, [
      'peSharedQESliderStatus',
    ]),
  },

  watch: {
    '$vuetify.theme.dark': {
      immediate: true,

      handler: function (val) {
        if (val) {
          createRgbVarsForThemes(this.$vuetify.theme.themes.dark)
        } else {
          createRgbVarsForThemes(this.$vuetify.theme.themes.light)
        }
      },
    },

    isLoggedIn (val) {
      // if the value of isLoggedIn has changed,
      // start or stop monitoring for inactivity
      if (val) {
        this.showNewsDialog = false // TODO: turn true for testing
      }
    },
  },

  created () {
    window.addEventListener('beforeunload', this.beforeUnloadHandler)
    window.addEventListener('sessionExpired', this.expireSession)
    window.addEventListener('storage', this.syncStatus)
    subscribeToAfterDeployNoticesChannel()
    this.trackUserActivity()
  },

  mounted () {
    // if the user is logged in, we'll check for a logout event
    if (this.isLoggedIn) {
      if (this.currentUser.news_seen === false) {
        this.showNewsDialog = true
      }
      this.updateLastActivity()
      this.refreshUser()
    }
  },

  beforeDestroy () {
    if (this.sessionCheckIntervalId) clearInterval(this.sessionCheckIntervalId)
    if (this.sessionExtensionWaitTimeoutId) clearTimeout(this.sessionExtensionWaitTimeoutId)
    window.removeEventListener('beforeunload', this.beforeUnloadHandler)
    window.removeEventListener('sessionExpired', this.expireSession)
    window.removeEventListener('storage', this.syncStatus)
    this.stopExpiryWarning()
  },

  methods: {
    acquireSessionExtensionLock () {
      const currentLock = this.getSessionExtensionLock()
      if (currentLock && currentLock.tabId !== this.sessionTabId) return false

      const lock = {
        expiresAt: Date.now() + SESSION_EXTENSION_LOCK_TTL_MS,
        tabId: this.sessionTabId,
      }

      window.localStorage.setItem(SESSION_EXTENSION_LOCK_KEY, JSON.stringify(lock))

      return this.getSessionExtensionLock()?.tabId === this.sessionTabId
    },

    applySessionExtensionEvent (value) {
      const sessionEvent = this.parseSessionEvent(value)
      if (!sessionEvent?.refreshTokenExpiresAt) return
      if (this.refreshTokenExpiresAt && sessionEvent.refreshTokenExpiresAt <= this.refreshTokenExpiresAt) return

      this.setRefreshTokenExpiresAt(sessionEvent.refreshTokenExpiresAt)
      this.setSessionSettings(sessionEvent.session)
      this.handleSessionInactive({ inactive: false })
      this.showAuthenticationFormsDialog = false
      this.stopExpiryWarning()
    },

    applySessionWarningEvent (value) {
      const warningEvent = this.parseSessionEvent(value)
      if (!this.isLoggedIn || this.showWarning || !warningEvent?.refreshTokenExpiresAt) return
      if (warningEvent.refreshTokenExpiresAt !== this.refreshTokenExpiresAt) return

      this.startExpiryWarning({ broadcast: false })
    },

    beforeUnloadHandler () {
      if (this.isLoggedIn) return

      this.handleSessionInactive({ inactive: true })
    },

    broadcastSessionExtension () {
      const sessionEvent = {
        refreshTokenExpiresAt: this.refreshTokenExpiresAt,
        session: {
          activity_window_in: this.sessionActivityWindowIn,
          warn_before_in: this.sessionWarnBeforeIn,
        },

        tabId: this.sessionTabId,
        updatedAt: Date.now(),
      }

      window.localStorage.setItem(SESSION_EXTENSION_EVENT_KEY, JSON.stringify(sessionEvent))
    },

    broadcastSessionWarning () {
      const warningEvent = {
        refreshTokenExpiresAt: this.refreshTokenExpiresAt,
        tabId: this.sessionTabId,
        updatedAt: Date.now(),
      }

      window.localStorage.setItem(SESSION_WARNING_EVENT_KEY, JSON.stringify(warningEvent))
    },

    callDisplayAlert (passedAlert) {
      this.displayAlert(passedAlert.type,
        passedAlert.message ? passedAlert.message : null,
        passedAlert.error ? passedAlert.error : null)
    },

    checkSessionExpiry () {
      if (!this.isLoggedIn || !this.refreshTokenExpiresAt || this.sessionWarnBeforeIn == null) return

      // Plenty of time left: make sure no stale warning is showing.
      if (this.refreshTokenExpiresAt - Date.now() > this.sessionWarnBeforeMs) {
        this.stopExpiryWarning()
        return
      }

      if (this.extendingSession || this.showWarning) return

      if (this.isUserRecentlyActive()) {
        // Active user near expiry: silently extend the session.
        this.extendingSession = true
        this.
          keepActive().
          then(success => { if (!success) this.startExpiryWarning() }).
          finally(_ => { this.extendingSession = false })
      } else {
        this.startExpiryWarning()
      }
    },

    clearSession () {
      this.handleSessionReturnTo({ returnTo: null })
    },

    deferSessionExpiryLogout () {
      if (this.deferredLogoutTimeoutId) return

      this.deferredLogoutTimeoutId = setTimeout(_ => {
        this.deferredLogoutTimeoutId = null

        if (this.refreshTokenExpiresAt > Date.now()) {
          this.stopExpiryWarning()
          return
        }

        if (this.isSessionExtensionLocked()) {
          this.deferSessionExpiryLogout()
          return
        }

        this.expireSession()
      }, SESSION_EXTENSION_WAIT_MS)
    },

    expireSession () {
      this.stopExpiryWarning()
      this.showAuthenticationFormsDialog = true
      this.handleSessionInactive({ inactive: true })
      this.logout(false)
    },

    extendSessionWithLock () {
      return this.
        refreshUserModel({ extend: 'extendRefreshToken', retainAuthOnError: true }).
        then(_ => {
          this.stopExpiryWarning()
          this.broadcastSessionExtension()
          return true
        }).
        catch(e => {
          Sentry.captureException(e)
          return false
        }).
        finally(_ => this.releaseSessionExtensionLock())
    },

    getSessionExtensionLock () {
      const lock = this.parseSessionEvent(window.localStorage.getItem(SESSION_EXTENSION_LOCK_KEY))

      if (!lock?.expiresAt || lock.expiresAt <= Date.now()) {
        window.localStorage.removeItem(SESSION_EXTENSION_LOCK_KEY)
        return null
      }

      return lock
    },

    isSessionExtensionLocked () {
      return !!this.getSessionExtensionLock()
    },

    isUserRecentlyActive () {
      const lastActivityAt = parseInt(window.localStorage.getItem('lastActivityAt'), 10)
      return !!lastActivityAt &&
        this.sessionActivityWindowMs > 0 &&
        (Date.now() - lastActivityAt) < this.sessionActivityWindowMs
    },

    keepActive () {
      if (this.acquireSessionExtensionLock()) return this.extendSessionWithLock()
      return this.
        waitForSessionExtension().
        then(success => {
          if (success) return true
          if (!this.acquireSessionExtensionLock()) return false
          return this.extendSessionWithLock()
        })
    },

    async logout (fromOtherTab = false) {
      this.
        logoutUserModel({ retainState: true }).
        then(_ => {
          this.clearSession()
          if (!fromOtherTab) window.localStorage.setItem('logout', Date.now())
        })
    },

    onCloseLoginForm () {
      this.showAuthenticationFormsDialog = false
    },

    parseSessionEvent (value) {
      if (!value) return null

      try {
        return JSON.parse(value)
      } catch (_) {
        return null
      }
    },

    refreshUser (fromOtherTab = false) {
      this.refreshUserModel().
        then(_ => {
          const path = this.sessionReturnTo
          // note: just catching here to avoid error in browser console. could do on all $router.push()
          if (path && this.$route.path !== path) this.$router.push(path).catch(_ => {})
          else if (this.$route.path !== '/') {
            if (fromOtherTab) {
              if (this.isSessionInactive) this.onCloseLoginForm()
              if (this.$route.path === '/login') this.$router.push('/')
            }
          }
        }).
        catch(error => {
          Sentry.captureException(error)
          this.expireSession()
        })
    },

    releaseSessionExtensionLock () {
      const currentLock = this.parseSessionEvent(window.localStorage.getItem(SESSION_EXTENSION_LOCK_KEY))
      if (currentLock?.tabId !== this.sessionTabId) return

      window.localStorage.removeItem(SESSION_EXTENSION_LOCK_KEY)
    },

    startExpiryWarning ({ broadcast = true } = {}) {
      this.showWarning = true
      if (broadcast) this.broadcastSessionWarning()
      if (this.warnCountdownIntervalId) clearInterval(this.warnCountdownIntervalId)

      const tick = () => {
        const secondsLeft = Math.max(0, Math.ceil((this.refreshTokenExpiresAt - Date.now()) / 1000))
        this.setRefreshTokenExpiresAtWarnLimit(secondsLeft)

        if (secondsLeft <= 0) {
          if (this.isSessionExtensionLocked()) {
            this.deferSessionExpiryLogout()
            return
          }

          this.expireSession()
        }
      }

      tick()
      this.warnCountdownIntervalId = setInterval(tick, 1000)
    },

    stopExpiryWarning () {
      this.showWarning = false
      if (this.deferredLogoutTimeoutId) {
        clearTimeout(this.deferredLogoutTimeoutId)
        this.deferredLogoutTimeoutId = null
      }
      if (this.warnCountdownIntervalId) {
        clearInterval(this.warnCountdownIntervalId)
        this.warnCountdownIntervalId = null
      }
    },

    syncStatus (event) {
      // logged out from another tab
      if (event.key === 'logout') this.logout(true)

      if (event.key === 'afteruserlogout') window.location.reload()
      // another tab successfully authenticated, so we'll restore window
      if (event.key === 'login') this.refreshUser(true)

      if (event.key === SESSION_EXTENSION_EVENT_KEY) this.applySessionExtensionEvent(event.newValue)
      if (event.key === SESSION_WARNING_EVENT_KEY) this.applySessionWarningEvent(event.newValue)
    },

    trackUserActivity () {
      const events = ['keydown', 'mousedown', 'touchstart']
      events.forEach(event => {
        document.addEventListener(event, this.updateLastActivity, { passive: true })
      })
      // scroll does not bubble; capture phase catches scrolls inside dialogs too
      document.addEventListener('scroll', this.updateLastActivity, { capture: true, passive: true })

      // The frontend owns activity tracking: periodically compare the
      // backend-issued refresh token expiry against now, and warn / extend.
      this.sessionCheckIntervalId = setInterval(this.checkSessionExpiry, SESSION_CHECK_INTERVAL_MS)
    },

    updateDrawerStatus (value) {
      this.appDrawerOpen = value
    },

    updateLastActivity () {
      window.localStorage.setItem('lastActivityAt', Date.now())
    },

    waitForSessionExtension () {
      return new Promise(resolve => {
        if (this.sessionExtensionWaitTimeoutId) clearTimeout(this.sessionExtensionWaitTimeoutId)

        this.sessionExtensionWaitTimeoutId = setTimeout(_ => {
          this.sessionExtensionWaitTimeoutId = null
          this.applySessionExtensionEvent(window.localStorage.getItem(SESSION_EXTENSION_EVENT_KEY))
          resolve(this.refreshTokenExpiresAt && this.refreshTokenExpiresAt - Date.now() > this.sessionWarnBeforeMs)
        }, SESSION_EXTENSION_WAIT_MS)
      })
    },

    ...mapActions([
      'handleSessionInactive',
      'handleSessionReturnTo',
      'logoutUserModel',
      'refreshUserModel',
    ]),

    ...mapMutations([
      'setRefreshTokenExpiresAt',
      'setRefreshTokenExpiresAtWarnLimit',
      'setSessionSettings',
    ]),
  },
}
</script>
